import { adminWalletConnection } from "./walletRpc";
import { config } from "./types";
import { assertWalletMessage } from "@megacorp-seeker/transaction-policy";
import { Buffer } from "buffer";
import { SystemProgram, VersionedTransaction, Transaction } from "@solana/web3.js";
import type { WalletContextState } from "@solana/wallet-adapter-react";

type SponsoringWallet = Pick<WalletContextState, "wallet" | "publicKey" | "connected" | "signTransaction">;
export async function signPurseSponsorship(wallet: SponsoringWallet, base64: string, expectedWallet: string, preview: (encoded: string, signed: boolean) => Promise<void> = previewSponsorship): Promise<string> {
  const started = performance.now();
  if (!wallet.connected || !wallet.publicKey || !wallet.signTransaction) throw new Error("Connect your selected sponsoring wallet before signing.");
  if (wallet.publicKey.toBase58() !== expectedWallet) throw new Error("The connected wallet changed. Reconnect the sponsoring wallet before signing.");
  const transaction = Transaction.from(Buffer.from(base64, "base64"));
  const expectedMessage = transaction.compileMessage();
  if (transaction.instructions.some(ix => ix.programId.equals(SystemProgram.programId) && ix.data.length >= 4 && ix.data.readUInt32LE(0) === 4))
    throw new Error("Refresh the sponsorship before approving it. Nothing was signed.");
  await preview(base64, false);
  const adapter = wallet.wallet?.adapter;
  let signed: Transaction;
  // Standard Adapter's sign-only method omits chain. Bind the request explicitly,
  // as the player wallet does, instead of relying on Phantom's selected network.
  if (adapter && "standard" in adapter && adapter.standard) {
    const standard = adapter.wallet;
    const chain = config.environment === "prod" ? "solana:mainnet" : "solana:devnet";
    const account = standard.accounts.find(value => value.address === expectedWallet);
    if (!account || !standard.chains.includes(chain) || !account.chains.includes(chain)) throw new Error(`The sponsoring account must support ${config.environment === "prod" ? "mainnet" : "devnet"}. No payment was signed.`);
    if (!("solana:signTransaction" in standard.features) || !account.features.includes("solana:signTransaction")) throw new Error("This sponsoring wallet cannot sign the payment.");
    console.info("[Purse sponsorship] requesting wallet approval", { chain, preparationMs: Math.round(performance.now() - started) });
    const requested = performance.now();
    const outputs = await standard.features["solana:signTransaction"].signTransaction({ account, chain,
      transaction: new Uint8Array(transaction.serialize({ requireAllSignatures: false, verifySignatures: false })) });
    console.info("[Purse sponsorship] wallet responded", { elapsedMs: Math.round(performance.now() - requested) });
    if (outputs.length !== 1 || !outputs[0]) throw new Error("The wallet did not return the sponsorship signature. No payment was submitted.");
    signed = Transaction.from(Buffer.from(outputs[0].signedTransaction));
  } else {
    console.info("[Purse sponsorship] requesting wallet approval", { adapter: adapter?.name, preparationMs: Math.round(performance.now() - started) });
    const requested = performance.now();
    signed = await wallet.signTransaction(transaction);
    console.info("[Purse sponsorship] wallet responded", { elapsedMs: Math.round(performance.now() - requested) });
  }
  if (adapter && adapter.publicKey?.toBase58() !== expectedWallet) throw new Error("The sponsoring wallet changed during approval. No payment was submitted.");
  assertWalletMessage(expectedMessage, signed.compileMessage());
  if (!signed.verifySignatures()) throw new Error("The sponsorship signature could not be verified. No payment was submitted.");
  const encoded = Buffer.from(signed.serialize()).toString("base64");
  await preview(encoded, true);
  if (adapter && adapter.publicKey?.toBase58() !== expectedWallet) throw new Error("The sponsoring wallet changed. No payment was submitted.");
  return encoded;
}

async function previewSponsorship(encoded: string, signed: boolean) {
  const rpc = adminWalletConnection();
  const result = await rpc.simulateTransaction(VersionedTransaction.deserialize(Buffer.from(encoded, "base64")), { sigVerify: signed, replaceRecentBlockhash: false, commitment: "confirmed" });
  if (result.value.err) throw new Error("The sponsorship preview failed. No payment was submitted.");
}

const approvalKey = (id: string, wallet: string) => `megacorp.purse.approval.v1.${wallet}.${id}`;
export function savedPurseApproval(id: string, wallet: string): string | null {
  try { return localStorage.getItem(approvalKey(id, wallet)); } catch { return null; }
}
export function retainPurseApproval(id: string, wallet: string, encoded: string) {
  const tx = Transaction.from(Buffer.from(encoded, "base64"));
  if (tx.feePayer?.toBase58() !== wallet || !tx.verifySignatures()) throw new Error("Saved sponsorship does not match this wallet.");
  localStorage.setItem(approvalKey(id, wallet), encoded);
  if (savedPurseApproval(id, wallet) !== encoded) throw new Error("Your approval could not be saved. No payment was submitted.");
}
export function clearPurseApproval(id: string, wallet: string) { localStorage.removeItem(approvalKey(id, wallet)); }
